<template>
    <div class="backoffice-subscriptions">
        <div class="page-header">
            <div class="left">
                <h2 class="page-title">{{ $page.props.lang.title || "Membership List" }}</h2>
            </div>
            <div class="right">
                <v-btn color="primary" dark @click="subscriptionsModal = true">
                    <v-icon small>mdi-plus</v-icon>
                    <span v-if="$vuetify.breakpoint.mdAndUp" class="ml-2">{{ $page.props.lang.newSubscription || "New membership" }}</span>
                </v-btn>
            </div>
        </div>
        <div class="filter-section">
            <v-text-field v-model="form.search" :label="$page.props.lang.filter_search || 'Search'" background-color="#FFFFFF" outlined dense prepend-inner-icon="mdi-magnify" class="table-search"></v-text-field>
        </div>
        <v-dialog v-model="subscriptionsModal" max-width="500px">
            <v-card>
                <div class="modal-header">
                    <h4 class="modal-headline">{{ $page.props.lang.newSubscription || "New membership" }}</h4>
                    <div class="tools">
                        <v-btn icon @click="subscriptionsModal = false"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                </div>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <p class="custom-label">{{$page.props.lang.sku|| 'SKU'}}</p>
                                <v-text-field v-model="newSubscription.sku" 
                                    :error-messages="responseErrors.sku" @focus="responseErrors.sku=''" 
                                    outlined dense :hide-details="!responseErrors.sku"></v-text-field>
                             </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{$page.props.lang.table_name|| 'Name'}}</p>
                                <v-text-field v-model="newSubscription.name" 
                                    :error-messages="responseErrors.name" @focus="responseErrors.name=''" 
                                    outlined dense :hide-details="!responseErrors.name"></v-text-field>
                             </v-col>
                             <v-col cols="8">
                                <v-switch class="mt-0 pt-0" v-model="newSubscription.is_limited" 
                                :label="$page.props.lang.limit_class || 'Limit Class Access'" hide-details>
                                </v-switch>
                             </v-col>  
                             <v-col cols="12" v-if="newSubscription.is_limited" class="mt-0 pt-0">
                                <p class="custom-label">{{$page.props.lang.allow_booking_for_classes || 'Allow booking for selected classes:'}}</p>
                                <v-select v-model="newSubscription.courses"
                                :items="courses" 
                                item-value="id"
                                item-text="title"
                                multiple
                                :error-messages="responseErrors.courses" @change="responseErrors.courses=''" 
                                 outlined dense :hide-details="!responseErrors.courses"></v-select>
                             </v-col>  
                             <v-col cols="12">
                                <p class="custom-label">{{$page.props.lang.table_description || 'Description' }}</p>
                                <v-textarea v-model="newSubscription.description" 
                                    :error-messages="responseErrors.description" @focus="responseErrors.description=''" 
                                    rows="3" outlined dense :hide-details="!responseErrors.description"></v-textarea>
                            </v-col> 
                            <v-col cols="12"> 
                                <p class="custom-label">{{ $page.props.lang.table_price || 'Price' }}</p>
                                <v-text-field v-model="newSubscription.price" 
                                    :suffix="$page.props.organization.settings.currency" 
                                    :error-messages="responseErrors.price" 
                                    :hide-details="!responseErrors.price"
                                    @focus="responseErrors.price=''"
                                    outlined dense ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.table_period || 'Period between each customer payment' }}</p>
                                <v-row>
                                    <v-col cols="4">
                                        <v-text-field v-model="newSubscription.period_value" 
                                            :error-messages="responseErrors.period_value" @focus="responseErrors.period_value=''" 
                                            outlined dense :hide-details="!responseErrors.period_value"></v-text-field>
                                    </v-col>
                                    <v-col cols="8">
                                        <v-select v-model="newSubscription.period_name" 
                                            :items="periodNameSelect" 
                                            :error-messages="responseErrors.period_name" @change="responseErrors.period_name=''" 
                                            class="period-select" outlined dense :hide-details="!responseErrors.period_name"></v-select>
                                    </v-col>  
                                </v-row>
                            </v-col>
                             <v-col cols="12"> 
                                <p class="custom-label">{{ $page.props.lang.membership_count || 'How many TIMES should the customer be charged?' }}</p>
                                <v-select v-model="newSubscription.count" 
                                            :items="membershipCount" 
                                            :error-messages="responseErrors.count" @change="responseErrors.count=''" 
                                            class="period-select" outlined dense :hide-details="!responseErrors.count"></v-select>
                            </v-col>
                            <v-col cols="12">
                                <p class="custom-label">{{ $page.props.lang.table_limit || 'Number of visits' }}</p>
                                <v-text-field v-model="newSubscription.limit" outlined dense hide-details></v-text-field>
                            </v-col>
                        </v-row>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn @click="subscriptionsModal = false" color="regular">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn @click="addSubscription" :loading="saving" color="primary">{{ $page.props.lang.save || "Save" }}</v-btn>
                        </v-card-actions>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-dialog>
        <div class="table-container">
            <v-data-table
            :headers="computedHeaders"
            :items="subscriptionsData.data"
            :server-items-length="subscriptionsData.total"
            :sort-by.sync="options.sortBy"
            :sort-desc.sync="options.sortDesc"
            item-key="item.id"
            :page="subscriptions.current_page"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            mobile-breakpoint="0"
            :footer-props="{
                    'items-per-page-options': [5, 10, 15]
                }"
            >
            <template v-slot:item.sku="{ item }">{{ item.sku }}</template>
                <template v-slot:item.name="{ item }">
                    <inertia-link :href="'/backoffice/subscriptions/view/'+item.id" class="name">
                        {{ item.name }}
                    </inertia-link>
                </template>
                <template v-slot:item.price="{ item }">
                    <p class="amount">
                        {{ item.price }} <span class="table-currency">{{ $page.props.organization.settings.currency }}</span> 
                    </p>
                </template>
                <template v-slot:item.period_value="{ item }">
                    {{ item.period_value }} {{ item.period_name }}
                </template>
                <template v-slot:item.limit_value="{ item }">
                    <span v-if="item.limit_value !== null">
                    {{ item.limit_value }}
                    </span>
                    <span v-else>{{$page.props.lang.unlimited || "Unlimited"}}</span>
                </template>
                
                <template v-slot:item.action="{ item }">
                        <v-btn v-if="item.is_archived===0 && item.count_of_members>0" @click="openShowArchiveModal(item)" :id="'archive' + item.id" class="archive-icon" icon>
                            <v-icon>mdi-archive-arrow-down-outline</v-icon>
                            <v-tooltip :activator="'#archive'+ item.id" bottom open-delay="500">
                                <span>{{$page.props.lang.archive || "Archive"}}</span>
                            </v-tooltip>
                        </v-btn>
                        <v-btn v-if="item.is_archived===1" @click="unArchive(item)" :id="'unarchive' + item.id" class="archive-icon" icon>
                            <v-icon>mdi-archive-arrow-up-outline</v-icon>
                            <v-tooltip :activator="'#unarchive'+ item.id" bottom open-delay="500">
                                <span>{{$page.props.lang.unarchive || "Unarchive"}}</span>
                            </v-tooltip>
                        </v-btn>
                        <v-btn v-if="item.is_archived===0 && item.count_of_members===0" @click="openDeleteModal(item)" :id="'delete' + item.id" class="delete-icon" icon>
                            <v-icon>mdi-trash-can-outline</v-icon>
                            <v-tooltip :activator="'#delete'+ item.id" bottom open-delay="500">
                                <span>{{$page.props.lang.delete || "Delete"}}</span>
                            </v-tooltip>
                        </v-btn>
                </template>
            </v-data-table>
            <ConfirmModal :open="showArchiveModal"
                @no="showArchiveModal = false" 
                @yes="archive" 
                @close="showArchiveModal = false"
                :title="$page.props.lang.archiveConfirmTitle || 'Archive confirmation'"
                :description="$page.props.lang.archiveConfirmDesc || 'Are You sure You want to archive subscription?'">
            </ConfirmModal>
            <ConfirmDelete :open="deleteModal" @no="deleteModal = false" @yes="deleteSubscription"></ConfirmDelete>
        </div>

    </div>
</template>

<script>
import AppLayout from '@/js/Layouts/AppLayout'
import Pagination from '@/js/Components/Pagination'
import ConfirmDelete from '@/js/Components/ConfirmDelete'
import ConfirmModal from '@/js/Components/ConfirmModal'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import EventBus from '@/js/eventbus'

export default {
    metaInfo() {
        return {
            title:  this.$page.props.lang.title || "List of Subscriptions"
        }
    },
    layout: AppLayout,
    components: {
        Pagination,
        ConfirmDelete,
        ConfirmModal
    },
    props: {
        subscriptions: Object,
        filters: Object,
        lang: Object,
        organization: Object,
        courses: Array
    },

    data(){
        return {
            form: {
                search: this.filters.search,
            },
            membershipCount: [
                {'value': 0, "text":  this.lang.until_canceled || 'Until canceled'}
            ],
            coursesData: this.courses,
            saving:false,
            subscriptionsData: this.subscriptions,
            subscriptionsHeaders: [
                {text: this.lang.sku || "SKU", value: 'sku', align: 'start' },
                {text: this.lang.table_name || 'Name', value: 'name', align: 'start'},
                {text: this.lang.table_price || 'Price', value: 'price', sortable: false},
                {text: this.lang.table_period || 'Accounting period', value: 'period_value', sortable: false, align: 'center'},
                {text: this.lang.table_limit || 'Limit', value: 'limit_value', sortable: false, align: 'center'},
                {text: this.lang.table_action || 'Action', value: 'action', sortable: false, align: 'end'}
            ],
            subscriptionsModal: false,
            newSubscription: {
                sku: null,
                name: '',
                description: '',
                period_value: 1,
                period_name: '',
                count: 0,
                limit: '',
                price: null,
                currency: '',
                courses: [],
                is_limited: false
            },
            resetSubscription: {
                sku: null,
                name: '',
                description: '',
                period_value: '',
                period_name: '',
                limit: '',
                price: null,
                currency: ''
            },
            messageObjects: [],
            responseErrors: {},
            resetErrors: {
                sku: '',
                name: '',
                description: '',
                period_name: '',
                period_value: '',
                currency: '',
                price: ''
            },
            deleteModal: false,
            subForDeleteId: null,
            subForArchiveId: null,
            options:{page:this.subscriptions.current_page},
            page: null,
            sortBy: [],
            sortDesc: [],
            search: null,
            showArchiveModal: false,
        }
    },
    computed: {
      periodNameSelect() {
          return [
            { value: 'Day', text: this.lang.day || 'Day' },
            { value: 'Week', text: this.lang.week || 'Week' },
            { value: 'Month', text: this.lang.month || 'Month' },
            { value: 'Year', text: this.lang.year || 'Year' }        
          ]
      },
      computedHeaders(){
          if (this.$vuetify.breakpoint.width > 599) {
                return this.subscriptionsHeaders
            } else {
                return this.subscriptionsHeaders.filter(item => item.value === 'name')
            }
      }
    },
    watch: {
        form: {
            handler: throttle(function() {
                let query = pickBy(this.form)
                this.$inertia.replace("/backoffice/subscriptions?"+(Object.keys(query).length ? "search="+query.search : "remember=forget"))
                .then(() => {
                    this.subscriptionsData = this.subscriptions
                })            
            }, 250),
            deep: true,
        },
        options:{
             
             handler (val, oldVal) {
                
                if(val.page != oldVal.page || val.sortBy != oldVal.sortBy || val.sortDesc != oldVal.sortDesc || val.itemsPerPage != oldVal.itemsPerPage){

                    let data = {}
                    let order = this.options.sortDesc[0]===false ? 'asc' : 'desc';

                    if (this.options.sortDesc.length>0 & oldVal.sortDesc != val.sortDesc) { 
                        data.order = order
                        data.sort = this.options.sortBy[0]
                        data.page = oldVal.page
                        
                    }else{
                        data.page = val.page;
                    }
                  
                    let obj = this.form;
                    
                    if (this.form.search!==null) { data.search = this.form.search}
                    data.items_per_page = this.options.itemsPerPage;
              
                    let finalData = Object.assign(data, obj);
                   
                    this.$inertia.get('/backoffice/subscriptions', pickBy(finalData), {preserveState: true});
                    this.options.page = this.subscriptions.current_page; 
                }
              
            },
            deep: true,
        },
        subscriptionsModal: function (value) {
            if (value===false) {
                this.newSubscription = Object.assign({}, this.resetSubscription)
                this.responseErrors = this.resetErrors
            }
        },
        subscriptions: function (value) {
            this.subscriptionsData = value
        }
    },
    created() {
        this.initPageAndSort();

        for(var i = 1; i <= 20; i++) {
            this.membershipCount.push({'value': i, 'text': (i + ' ' + (this.$page.props.lang.times || 'times'))})
        }
    },
    methods: {
        getResults(page = 1) {
            var self = this
            axios.get('/backoffice/subscriptions/get?page=' + page)
            .then(response => {
                self.subscriptions = response.data
            })
        },
        addSubscription() {
            this.saving = true;
            axios.post('/backoffice/subscriptions/store', this.newSubscription)
                .then(response => {
                    this.saving = false;
                    if (response.data.status==="OK") {
                       
                        this.$inertia.reload({only:['subscriptions']})
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                        this.subscriptionsModal = false
                    }
                })
                .catch(error => {                   
                    this.saving = false;
                    EventBus.$emit('SHOW_NOTIFICATION',{ 
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    this.responseErrors = error.response.data.errors
                })
        },
        openDeleteModal(item) {
            this.deleteModal = true
            this.subForDeleteId = item.id
        },
        deleteSubscription() {
            axios.post('/backoffice/subscriptions/delete', {id: this.subForDeleteId})
                .then(response => {
                    if(response.data.status==="OK") {
                        // this.subscriptionsData.data.splice(this.subscriptionsData.data.findIndex(i => i.id === this.subForDeleteId), 1)
                        this.$inertia.reload({only:['subscriptions']})
                        EventBus.$emit('SHOW_NOTIFICATION',{
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                    }else{
                        EventBus.$emit('SHOW_NOTIFICATION',{ 
                            message: response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    }
                })
                .catch(function (error) {
                    EventBus.$emit('SHOW_NOTIFICATION',{ 
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                })
            this.deleteModal = false
        },

         initPageAndSort() {
            let pageUrl = new URLSearchParams(window.location.search)
            let sortDesc = pageUrl.get('order')
            let sortBy = pageUrl.get('sort')
            let page = pageUrl.get('page')
            this.page = page ? page : null
            sortBy ? this.sortBy[0] = sortBy : ''
            if (sortDesc) {
                sortDesc==='asc' ? this.sortDesc[0] = false : this.sortDesc[0] = true
            }
        },
        
        openShowArchiveModal(item) {
            this.showArchiveModal = true
            this.subForArchiveId = item.id
        },
        archive() {
            axios.post('/backoffice/subscriptions/archive', {id: this.subForArchiveId})
                .then(response => {
                    if(response.data.status==="OK") {
                        this.$inertia.reload({only:['subscriptions']})
                        this.showArchiveModal = false
                        EventBus.$emit('SHOW_NOTIFICATION',{ 
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                    }else{
                        EventBus.$emit('SHOW_NOTIFICATION',{
                            message: response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    }
                })
                .catch(function (error) {
                    EventBus.$emit('SHOW_NOTIFICATION',{ 
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                })
        },
        unArchive(item) {
            axios.post('/backoffice/subscriptions/archive', {id: item.id})
                .then(response => {
                    if(response.data.status==="OK") {
                        this.$inertia.reload({only:['subscriptions']})
                        EventBus.$emit('SHOW_NOTIFICATION',{ 
                                message: response.data.message,
                                color: 'success',
                                timeout: 5000
                            })
                    }else{
                        EventBus.$emit('SHOW_NOTIFICATION',{
                            message: response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                    }
                })
                .catch(function (error) {
                    EventBus.$emit('SHOW_NOTIFICATION',{
                            message: error.response.data.message,
                            color: 'error',
                            timeout: 10000
                        })
                })

        }
    }
}
</script>